import Dashboard from './dashboard';
import MachineGroup from './machineGroup';
import Machine from './machine';
import Factory from './factory';
import EntityHelper from '../helpers/entity';
import Manager from './roles/manager';
import Administrator from './roles/administrator';
import Role from './role';
import Operator from './roles/operator';

function createRole(name, params) {
  if (typeof name !== 'string') return undefined;
  if (name === 'administrator') return new Administrator(params?.id);
  if (name === 'manager') return new Manager(params?.id);
  if (name === 'operator') return new Operator(params?.id);
  return new Role({ ...params });
}
const collectionOf = (items, Entity, locale) => {
  if (!Array.isArray(items)) return [];
  const hasInvalidData = items.some(
    (item) => typeof item !== 'object' || Array.isArray(item),
  );
  if (hasInvalidData) return [];

  return items.map((item) => new Entity({ ...item, locale }));
};

const actions = {
  create: 'create', read: 'read', update: 'update', delete: 'delete',
};
const addToCollection = (item, collection, Entity) => {
  EntityHelper.validateData(item);
  const result = [...collection];
  result.push(new Entity({ ...item }));
  return result;
};

export default class User {
  constructor(data) {
    EntityHelper.validateData(data);
    this.data = data;
    const {
      role,
      id,
      fullName,
      firstName,
      lastName,
      groups,
      dashboards,
      machines,
      factories,
      startingDashboard,
      locale,
      email,
      authMode,
    } = data;

    this.locale = locale;
    this.role = createRole(role?.name, { ...role, locale });
    this.id = EntityHelper.setId(id);
    this.groups = collectionOf(groups, MachineGroup, this.locale);
    this.dashboards = collectionOf(dashboards, Dashboard, this.locale);
    this.machines = collectionOf(machines, Machine, this.locale);
    this.factories = collectionOf(factories, Factory, this.locale);
    this.fullName = fullName;
    this.firstName = firstName;
    this.lastName = lastName;
    this.startingDashboard = EntityHelper.setId(startingDashboard);
    this.email = email;
    // How this user authenticates. A payload that did not ask for the field carries
    // none: treated as 'password'.
    //
    // `null` n'est pas `undefined`. Le serveur ne rend jamais ce champ vide, donc une charge
    // qui l'a demandé et le reçoit vide décrit une lecture qui a échoué, pas un compte à mot
    // de passe. Confondre les deux faisait affirmer « mot de passe » d'une personne dont on
    // ne savait rien, et l'écran cochait la case avec l'aplomb d'une certitude.
    if (authMode === null) {
      console.error('User entity: authMode came back empty, the read is partial', { id });
    }
    this.authMode = authMode ?? 'password';
  }

  /**
   * A SSO user has no native account, so no password to change and no address to
   * change either: the guard matches the directory email against it.
   */
  signsInWithSso() {
    return this.authMode === 'sso';
  }

  canDo(action, targetUser) {
    if (!action) return false;
    const asMyself = this.equals(targetUser);
    const forbiddenActionOnMyself = [actions.create, actions.delete].includes(action);
    if (asMyself && forbiddenActionOnMyself) return false;
    return this.role.can(action, targetUser.role);
  }

  equals(user) {
    return this.id === user.id && this.role.equals(user.role);
  }

  addDashboard(item) {
    this.dashboards = addToCollection(item, this.dashboards, Dashboard);
  }

  addMachine(item) {
    this.machines = addToCollection(item, this.machines, Machine);
  }

  addGroup(item) {
    this.groups = addToCollection(item, this.groups, MachineGroup);
  }

  defaultDashboard() {
    if (!this.dashboards) return undefined;
    return this.dashboards.find((dashboard) => this.startingDashboard.toString() === dashboard.id);
  }
}
